import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../api/client';
import fetchTaskListAllPages from '../api/fetchTaskListAllPages';
import { taskNavRef } from '../utils/taskNav';
import { studentLabel, studentLabelWithExamYear, studentLabelWithLogin } from '../utils/studentLabel';
import StudentMessengerCell from '../components/StudentMessengerCell';
import StudentTelemostCell from '../components/StudentTelemostCell';
import Modal from '../components/Modal';
import { formatMoney } from './billingShared';
import { useAuth } from '../context/AuthContext';

const STUDENTS_SORT_MODE_KEY = 'teacher-students-sort-mode';

function loadStudentsSortMode() {
  try {
    return localStorage.getItem(STUDENTS_SORT_MODE_KEY) === 'alpha' ? 'alpha' : 'custom';
  } catch {
    return 'custom';
  }
}

const CABINET_TITLES = {
  students: {
    title: 'Ученики',
    subtitle: 'Список учеников и текущая работа.',
  },
  tasklists: {
    title: 'Списки',
    subtitle: 'Списки, выданные ученикам, и заготовки.',
  },
  lessons: {
    title: 'Уроки',
    subtitle: 'Активные и недавние занятия.',
  },
};

const STUDENT_STATUS_LABELS = {
  IN_PROGRESS: 'В работе',
  DONE: 'Завершён',
};

const TEACHER_STATUS_LABELS = {
  NOT_CHECKED: 'Не проверен',
  CHECKED: 'Проверен',
  ARCHIVED: 'В архиве',
};

/** Локальная дата YYYY-MM-DD (не UTC). */
function toLocalDateInputValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function defaultIssuedFromDate() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - 8);
  return toLocalDateInputValue(d);
}

function emptyTaskListFilters() {
  return {
    title: '',
    student: '',
    issuedFrom: defaultIssuedFromDate(),
    issuedTo: '',
    deadlineFrom: '',
    deadlineTo: '',
    studentStatus: '',
    teacherStatus: '',
  };
}

/** Сравнивает ISO datetime с YYYY-MM-DD по локальному календарному дню. */
function localDayKey(isoOrDate) {
  if (!isoOrDate) return null;
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return null;
  return toLocalDateInputValue(d);
}

function listStudentSearchText(lst) {
  if (lst?.is_template || !lst?.student_id) return 'заготовка';
  const info = lst.student_info;
  const label = studentLabel(info);
  const username = (info?.username || lst.student || '').toString().trim();
  return [label, username, info?.first_name, info?.last_name, info?.alias]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function listStudentDisplay(lst) {
  if (lst?.is_template || !lst?.student_id) return 'Заготовка';
  const label = studentLabel(lst.student_info);
  if (label) return label;
  return lst.student || '—';
}

function matchesTaskListFilters(lst, filters) {
  const titleQ = (filters.title || '').trim().toLowerCase();
  if (titleQ && !(lst.title || '').toLowerCase().includes(titleQ)) return false;

  const studentQ = (filters.student || '').trim().toLowerCase();
  if (studentQ && !listStudentSearchText(lst).includes(studentQ)) return false;

  const issuedDay = localDayKey(lst.issued_at);
  if (filters.issuedFrom) {
    if (!issuedDay || issuedDay < filters.issuedFrom) return false;
  }
  if (filters.issuedTo) {
    if (!issuedDay || issuedDay > filters.issuedTo) return false;
  }

  const hasDeadlineFilter = Boolean(filters.deadlineFrom || filters.deadlineTo);
  if (hasDeadlineFilter) {
    const deadlineDay = localDayKey(lst.deadline_at);
    if (!deadlineDay) return false;
    if (filters.deadlineFrom && deadlineDay < filters.deadlineFrom) return false;
    if (filters.deadlineTo && deadlineDay > filters.deadlineTo) return false;
  }

  if (filters.studentStatus && lst.student_status !== filters.studentStatus) return false;
  if (filters.teacherStatus && lst.teacher_status !== filters.teacherStatus) return false;

  return true;
}

function cabinetTabFromPath(pathname) {
  if (pathname.startsWith('/teacher/lists')) return 'tasklists';
  if (pathname.startsWith('/teacher/lessons')) return 'lessons';
  return 'students';
}

/**
 * Рабочее место преподавателя:
 * - /teacher — ученики;
 * - /teacher/lists — списки задач;
 * - /teacher/lessons — уроки.
 */
export default function TeacherDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const activeTab = cabinetTabFromPath(location.pathname);
  const pageMeta = CABINET_TITLES[activeTab];

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentsError, setStudentsError] = useState('');
  const [layoutItems, setLayoutItems] = useState([]);
  const [reorderMode, setReorderMode] = useState(false);
  const [layoutError, setLayoutError] = useState('');
  const [layoutSaving, setLayoutSaving] = useState(false);
  const reorderModeRef = useRef(false);
  const [studentsSortMode, setStudentsSortMode] = useState(loadStudentsSortMode);

  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [studentOverview, setStudentOverview] = useState(null);
  const [studentOverviewLoading, setStudentOverviewLoading] = useState(false);
  const [studentOverviewError, setStudentOverviewError] = useState('');

  const [taskLists, setTaskLists] = useState([]);
  const [taskListsLoading, setTaskListsLoading] = useState(true);
  const [taskListsError, setTaskListsError] = useState('');
  const [taskListFilters, setTaskListFilters] = useState(() => emptyTaskListFilters());

  const filteredTaskLists = useMemo(
    () => taskLists.filter((lst) => matchesTaskListFilters(lst, taskListFilters)),
    [taskLists, taskListFilters],
  );

  const [editingList, setEditingList] = useState(null); // null — ничего, {} — новый, объект — существующий
  const [listForm, setListForm] = useState({
    title: '',
    studentId: '',
    deadline: '',
    items: [], // { taskId, slug, title, teacherNote }
  });
  const [listSaving, setListSaving] = useState(false);
  const [listFormError, setListFormError] = useState('');

  const [taskSearch, setTaskSearch] = useState('');
  const [taskSearchResults, setTaskSearchResults] = useState([]);
  const [taskSearchLoading, setTaskSearchLoading] = useState(false);
  const [taskSearchError, setTaskSearchError] = useState('');

  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(true);
  const [lessonsError, setLessonsError] = useState('');
  const [lessonReportSessionId, setLessonReportSessionId] = useState(null);
  const [lessonReport, setLessonReport] = useState(null);
  const [lessonReportLoading, setLessonReportLoading] = useState(false);
  const [endingLessonId, setEndingLessonId] = useState(null);
  const [endLessonTarget, setEndLessonTarget] = useState(null);
  const [dashIssueOpen, setDashIssueOpen] = useState(false);
  const [dashIssueTitle, setDashIssueTitle] = useState('');
  const [dashIssueDeadline, setDashIssueDeadline] = useState('');
  const [dashIssueSaving, setDashIssueSaving] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);

  async function downloadTelemostExport() {
    setExportBusy(true);
    try {
      const res = await api.get('/teacher/students/telemost-export/');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], {
        type: 'application/json;charset=utf-8',
      });
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = 'telemost-students.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch {
      setStudentsError('Не удалось скачать список для микшера.');
    } finally {
      setExportBusy(false);
    }
  }

  const fetchStudents = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!silent) {
      setStudentsLoading(true);
      setStudentsError('');
    }
    try {
      const res = await api.get('/teacher/students/');
      const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
      setStudents(data);
      if (!silent) setStudentsError('');
    } catch {
      if (!silent) setStudentsError('Не удалось загрузить список учеников.');
    } finally {
      if (!silent) setStudentsLoading(false);
    }
  }, []);

  const fetchLayout = useCallback(async () => {
    try {
      const res = await api.get('/teacher/students/layout/');
      setLayoutItems(Array.isArray(res.data) ? res.data : []);
      setLayoutError('');
    } catch {
      setLayoutError('Не удалось загрузить порядок списка.');
    }
  }, []);

  useEffect(() => {
    reorderModeRef.current = reorderMode;
  }, [reorderMode]);

  useEffect(() => {
    fetchStudents();
    fetchLayout();
    fetchTaskLists();
    fetchLessons();
  }, [fetchStudents, fetchLayout]);

  async function fetchTaskLists() {
    setTaskListsLoading(true);
    setTaskListsError('');
    try {
      const all = await fetchTaskListAllPages('/teacher/task-lists/');
      setTaskLists(Array.isArray(all) ? all : []);
    } catch {
      setTaskListsError('Не удалось загрузить списки задач преподавателя.');
    } finally {
      setTaskListsLoading(false);
    }
  }

  function updateTaskListFilter(key, value) {
    setTaskListFilters((prev) => ({ ...prev, [key]: value }));
  }

  function resetTaskListFilters() {
    setTaskListFilters(emptyTaskListFilters());
  }

  async function fetchLessons() {
    setLessonsLoading(true);
    setLessonsError('');
    try {
      const res = await api.get('/teacher/lesson-sessions/');
      setLessons(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    } catch {
      setLessonsError('Не удалось загрузить уроки.');
    } finally {
      setLessonsLoading(false);
    }
  }

  function defaultHomeworkTitle() {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    return `ДЗ ${dd}.${mm}`;
  }

  function requestEndLessonFromList(session) {
    const draft = session?.homework_draft || [];
    if (draft.length > 0) {
      setEndLessonTarget(session);
      return;
    }
    if (!window.confirm('Завершить урок?')) return;
    void endLessonFromList(session.id);
  }

  async function endLessonFromList(sessionId) {
    setEndingLessonId(sessionId);
    try {
      await api.post(`/tasks/lesson-sessions/${sessionId}/end/`);
      setEndLessonTarget(null);
      await fetchLessons();
      await fetchStudents({ silent: true });
      if (lessonReportSessionId === sessionId) {
        try {
          const res = await api.get(`/teacher/lesson-sessions/${sessionId}/report/`);
          setLessonReport(res.data);
        } catch {
          /* ignore */
        }
      }
    } catch {
      alert('Не удалось завершить урок.');
    } finally {
      setEndingLessonId(null);
    }
  }

  function openDashIssueAndEnd() {
    if (!endLessonTarget) return;
    setDashIssueTitle(defaultHomeworkTitle());
    setDashIssueDeadline('');
    setDashIssueOpen(true);
  }

  async function submitDashIssueAndEnd() {
    if (!endLessonTarget) return;
    setDashIssueSaving(true);
    try {
      const payload = { title: dashIssueTitle.trim() || defaultHomeworkTitle() };
      if (dashIssueDeadline) {
        payload.deadline_at = `${dashIssueDeadline}T23:59:59`;
      }
      await api.post(`/teacher/lesson-sessions/${endLessonTarget.id}/issue-homework/`, payload);
      await api.post(`/tasks/lesson-sessions/${endLessonTarget.id}/end/`);
      setDashIssueOpen(false);
      setEndLessonTarget(null);
      await fetchLessons();
      await fetchStudents({ silent: true });
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось выдать ДЗ.';
      alert(typeof msg === 'string' ? msg : 'Не удалось выдать ДЗ.');
    } finally {
      setDashIssueSaving(false);
    }
  }

  /** Обновляем статус «К работе» без полноэкранной загрузки, пока открыта вкладка учеников. */
  useEffect(() => {
    if (activeTab !== 'students') return;
    const id = setInterval(() => {
      fetchStudents({ silent: true });
      if (!reorderModeRef.current) {
        fetchLayout();
      }
    }, 15000);
    return () => clearInterval(id);
  }, [activeTab, fetchStudents, fetchLayout]);

  const studentRows = useMemo(() => {
    if (studentsSortMode === 'alpha') {
      return [...students]
        .sort((a, b) => {
          const la = studentLabel(a) || a.username || '';
          const lb = studentLabel(b) || b.username || '';
          return la.localeCompare(lb, 'ru', { sensitivity: 'base' });
        })
        .map((s) => ({ kind: 'student', key: `s-${s.id}`, student: s }));
    }
    const byId = new Map(students.map((s) => [s.id, s]));
    const rows = [];
    const seen = new Set();
    for (const item of layoutItems) {
      if (item.type === 'divider') {
        rows.push({
          kind: 'divider',
          key: item.id != null ? `d-${item.id}` : `d-tmp-${rows.length}`,
          id: item.id,
          title: item.title || '',
        });
      } else if (item.type === 'student' && byId.has(item.id)) {
        rows.push({ kind: 'student', key: `s-${item.id}`, student: byId.get(item.id) });
        seen.add(item.id);
      }
    }
    for (const s of students) {
      if (!seen.has(s.id)) {
        rows.push({ kind: 'student', key: `s-${s.id}`, student: s });
      }
    }
    return rows;
  }, [layoutItems, students, studentsSortMode]);

  function setStudentsSortModeAndPersist(mode) {
    setStudentsSortMode(mode);
    if (mode === 'alpha') {
      setReorderMode(false);
    }
    try {
      localStorage.setItem(STUDENTS_SORT_MODE_KEY, mode);
    } catch {
      /* ignore */
    }
  }

  function layoutPayloadFromRows(rows) {
    return rows.map((row) => {
      if (row.kind === 'divider') {
        const item = { type: 'divider', title: row.title || '' };
        if (row.id != null) item.id = row.id;
        return item;
      }
      return { type: 'student', id: row.student.id };
    });
  }

  async function saveLayout(nextPayload) {
    setLayoutSaving(true);
    setLayoutError('');
    setLayoutItems(nextPayload.map((item, idx) => (
      item.type === 'divider' && item.id == null
        ? { ...item, _tmp: idx }
        : item
    )));
    try {
      const res = await api.put('/teacher/students/layout/', nextPayload);
      const data = Array.isArray(res.data) ? res.data : [];
      setLayoutItems(data);
      const order = data.filter((i) => i.type === 'student').map((i) => i.id);
      setStudents((prev) => {
        const byId = new Map(prev.map((s) => [s.id, s]));
        const ordered = order.map((id) => byId.get(id)).filter(Boolean);
        const rest = prev.filter((s) => !order.includes(s.id));
        return [...ordered, ...rest];
      });
    } catch {
      setLayoutError('Не удалось сохранить порядок.');
      await fetchLayout();
    } finally {
      setLayoutSaving(false);
    }
  }

  function moveLayoutRow(index, delta) {
    if (layoutSaving) return;
    const nextIndex = index + delta;
    if (nextIndex < 0 || nextIndex >= studentRows.length) return;
    const nextRows = [...studentRows];
    const tmp = nextRows[index];
    nextRows[index] = nextRows[nextIndex];
    nextRows[nextIndex] = tmp;
    saveLayout(layoutPayloadFromRows(nextRows));
  }

  function addLayoutDivider() {
    if (layoutSaving) return;
    saveLayout([...layoutPayloadFromRows(studentRows), { type: 'divider', title: '' }]);
  }

  function removeLayoutDivider(index) {
    if (layoutSaving) return;
    const nextRows = studentRows.filter((_, i) => i !== index);
    saveLayout(layoutPayloadFromRows(nextRows));
  }

  function setDividerTitleLocal(index, title) {
    const nextRows = studentRows.map((row, i) => (
      i === index && row.kind === 'divider' ? { ...row, title } : row
    ));
    setLayoutItems(layoutPayloadFromRows(nextRows));
  }

  function commitDividerTitle(index, rawTitle) {
    if (layoutSaving) return;
    const title = String(rawTitle ?? '').trim();
    const nextRows = studentRows.map((row, i) => (
      i === index && row.kind === 'divider' ? { ...row, title } : row
    ));
    saveLayout(layoutPayloadFromRows(nextRows));
  }

  async function loadStudentOverview(studentId) {
    setSelectedStudentId(studentId);
    setStudentOverview(null);
    setStudentOverviewError('');
    setStudentOverviewLoading(true);
    try {
      const res = await api.get(`/teacher/students/${studentId}/overview/`);
      setStudentOverview(res.data);
    } catch {
      setStudentOverviewError('Не удалось загрузить сводку по ученику.');
    } finally {
      setStudentOverviewLoading(false);
    }
  }

  async function handleStartLesson(studentId) {
    try {
      const res = await api.post('/teacher/lesson-sessions/', { student_id: studentId });
      await fetchLessons();
      await fetchStudents({ silent: true });
      if (selectedStudentId === studentId) {
        await loadStudentOverview(studentId);
      }
      return res.data;
    } catch (e) {
      alert(e.response?.data?.detail || 'Не удалось запустить урок.');
      return null;
    }
  }

  async function requestEndLessonForStudent(student) {
    const sessionId = student.active_lesson_session_id;
    if (!sessionId) return;
    let session = lessons.find((s) => s.id === sessionId);
    if (!session) {
      try {
        const res = await api.get(`/teacher/lesson-sessions/${sessionId}/`);
        session = res.data;
      } catch {
        alert('Не удалось загрузить урок.');
        return;
      }
    }
    requestEndLessonFromList(session);
  }

  function getRegisterInviteUrl() {
    const username = user?.username;
    if (!username) return '';
    const path = `/register?teacher=${encodeURIComponent(username)}`;
    if (typeof window === 'undefined') return path;
    return `${window.location.origin}${path}`;
  }

  function startEditList(lst) {
    setEditingList(lst);
    setListForm({
      title: lst.title || '',
      studentId: '', // менять ученика для существующего списка пока не даём
      deadline: lst.deadline_at ? lst.deadline_at.slice(0, 10) : '',
      items: Array.isArray(lst.items)
        ? lst.items.map((it) => ({
            taskId: it.task?.id,
            slug: it.task ? taskNavRef(it.task) : '',
            title: it.task?.title,
            teacherNote: it.teacher_note || '',
          }))
        : [],
    });
    setListFormError('');
    setTaskSearch('');
    setTaskSearchResults([]);
    setTaskSearchError('');
  }

  function addTaskToList(task) {
    if (!task?.id) return;
    setListForm((prev) => {
      if (prev.items.some((it) => it.taskId === task.id)) return prev;
      return {
        ...prev,
        items: [
          ...prev.items,
          {
            taskId: task.id,
            slug: taskNavRef(task),
            title: task.title,
            teacherNote: '',
          },
        ],
      };
    });
  }

  function removeTaskFromList(taskId) {
    setListForm((prev) => ({
      ...prev,
      items: prev.items.filter((it) => it.taskId !== taskId),
    }));
  }

  function updateItemNote(taskId, note) {
    setListForm((prev) => ({
      ...prev,
      items: prev.items.map((it) =>
        it.taskId === taskId ? { ...it, teacherNote: note } : it
      ),
    }));
  }

  async function searchTasks() {
    if (!taskSearch.trim()) {
      setTaskSearchResults([]);
      setTaskSearchError('');
      return;
    }
    setTaskSearchLoading(true);
    setTaskSearchError('');
    try {
      const params = new URLSearchParams();
      params.set('search', taskSearch.trim());
      const res = await api.get(`/tasks/editor/?${params.toString()}`);
      const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
      setTaskSearchResults(data);
    } catch {
      setTaskSearchError('Не удалось выполнить поиск задач.');
      setTaskSearchResults([]);
    } finally {
      setTaskSearchLoading(false);
    }
  }

  async function handleSaveList() {
    if (!editingList) return;
    if (!listForm.title.trim()) {
      setListFormError('Введите название списка.');
      return;
    }
    if (!editingList.id && !listForm.studentId) {
      setListFormError('Выберите ученика.');
      return;
    }

    setListSaving(true);
    setListFormError('');
    try {
      const payload = {
        title: listForm.title.trim(),
        deadline_at: listForm.deadline || null,
        items: listForm.items.map((it, index) => ({
          task_id: it.taskId,
          position: index,
          teacher_note: it.teacherNote || '',
        })),
      };
      if (!editingList.id) {
        payload.student = Number(listForm.studentId);
        await api.post('/teacher/task-lists/', payload);
      } else {
        await api.patch(`/teacher/task-lists/${editingList.id}/`, payload);
      }
      await fetchTaskLists();
      setEditingList(null);
    } catch (e) {
      const msg = e.response?.data;
      if (typeof msg === 'object' && msg !== null) {
        const first = Object.entries(msg)[0];
        setListFormError(
          first ? `${first[0]}: ${Array.isArray(first[1]) ? first[1][0] : first[1]}` : 'Ошибка сохранения списка.'
        );
      } else {
        setListFormError('Ошибка сохранения списка.');
      }
    } finally {
      setListSaving(false);
    }
  }

  function renderStudentsTab() {
    const showCustomOrder = studentsSortMode === 'custom';
    let studentNum = 0;
    return (
      <div className="teacher-tab teacher-students">
        <div className="teacher-tab-main">
          <div className="teacher-students-header-row">
            {students.length > 0 && (
              <div className="teacher-students-header-actions">
                {!reorderMode && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    onClick={downloadTelemostExport}
                    disabled={exportBusy}
                    title="JSON для программы telemost-mixer"
                  >
                    {exportBusy ? 'Скачивание…' : 'Список для микшера'}
                  </button>
                )}
                <div className="teacher-students-sort-toggle" role="group" aria-label="Сортировка списка">
                  <button
                    type="button"
                    className={`btn btn-sm ${studentsSortMode === 'custom' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setStudentsSortModeAndPersist('custom')}
                    disabled={layoutSaving}
                  >
                    Мой порядок
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${studentsSortMode === 'alpha' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setStudentsSortModeAndPersist('alpha')}
                    disabled={layoutSaving}
                  >
                    А–Я
                  </button>
                </div>
                {showCustomOrder && reorderMode && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    onClick={addLayoutDivider}
                    disabled={layoutSaving}
                  >
                    Добавить линию
                  </button>
                )}
                {showCustomOrder && (
                  <button
                    type="button"
                    className={`btn btn-sm ${reorderMode ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => {
                      setReorderMode((prev) => !prev);
                      setLayoutError('');
                    }}
                    disabled={layoutSaving}
                  >
                    {reorderMode ? 'Готово' : 'Порядок'}
                  </button>
                )}
              </div>
            )}
          </div>
          <p className="text-secondary teacher-students-hint" style={{ maxWidth: '44rem', marginBottom: '1rem' }}>
            {reorderMode
              ? 'Стрелками меняйте порядок строк. Линии группируют учеников; подпись необязательна. Изменения сохраняются сразу.'
              : (
                <>
                  Чтобы появилась кнопка «К работе», ученик должен открыть задачу{' '}
                  <strong>из списка урока или ДЗ</strong> (страница <code>/work/…</code> с параметром{' '}
                  <code>list=…</code> в адресе) и дождаться загрузки редактора. На занятии список «Урок ДД.ММ»
                  создаётся сам при «Начать урок»; ученик заходит по баннеру «Урок идёт». Список обновляется
                  автоматически каждые 15 с. Та же ссылка есть в карточке ученика («Подключиться к работе»).
                </>
              )}
          </p>
          {studentsError && <div className="alert alert-error">{studentsError}</div>}
          {layoutError && <div className="alert alert-error">{layoutError}</div>}
          {studentsLoading ? (
            <p className="text-secondary">Загрузка списка учеников...</p>
          ) : students.length === 0 ? (
            <p className="text-secondary">Пока нет учеников в связке с вами.</p>
          ) : (
            <table className={`table teacher-students-table${reorderMode ? ' is-reordering' : ''}`}>
              <thead>
                <tr>
                  {reorderMode && <th className="teacher-students-order-col">Порядок</th>}
                  {!reorderMode && <th className="teacher-students-num-col">№</th>}
                  <th>Ученик</th>
                  {!reorderMode && (
                    <>
                      <th>Чат</th>
                      <th className="teacher-students-telemost-col" title="Телемост" aria-label="Телемост">
                        <i className="fa-solid fa-video" aria-hidden />
                      </th>
                      <th>Баланс</th>
                      <th title="Несданные списки">Списки</th>
                      <th>Онлайн</th>
                      <th>Действия</th>
                    </>
                  )}
                  {reorderMode && <th>Подпись / действия</th>}
                </tr>
              </thead>
              <tbody>
                {studentRows.map((row, index) => {
                  if (row.kind === 'divider') {
                    const colSpan = reorderMode ? 1 : 8;
                    return (
                      <tr key={row.key} className="teacher-students-divider-row">
                        {reorderMode && (
                          <td className="teacher-students-order-col">
                            <div className="teacher-students-order-btns">
                              <button
                                type="button"
                                className="btn btn-xs btn-outline"
                                onClick={() => moveLayoutRow(index, -1)}
                                disabled={layoutSaving || index === 0}
                                title="Выше"
                                aria-label="Выше"
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="btn btn-xs btn-outline"
                                onClick={() => moveLayoutRow(index, 1)}
                                disabled={layoutSaving || index === studentRows.length - 1}
                                title="Ниже"
                                aria-label="Ниже"
                              >
                                ↓
                              </button>
                            </div>
                          </td>
                        )}
                        <td colSpan={reorderMode ? 1 : colSpan}>
                          <div className="teacher-students-divider">
                            <span className="teacher-students-divider-line" aria-hidden />
                            {reorderMode ? (
                              <input
                                type="text"
                                className="form-input teacher-students-divider-input"
                                value={row.title}
                                placeholder="Подпись (необязательно)"
                                maxLength={80}
                                aria-label="Подпись линии"
                                disabled={layoutSaving}
                                onChange={(e) => setDividerTitleLocal(index, e.target.value)}
                                onBlur={(e) => commitDividerTitle(index, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.blur();
                                  }
                                }}
                              />
                            ) : (
                              row.title
                                ? <span className="teacher-students-divider-title">{row.title}</span>
                                : null
                            )}
                            <span className="teacher-students-divider-line" aria-hidden />
                          </div>
                        </td>
                        {reorderMode && (
                          <td>
                            <button
                              type="button"
                              className="btn btn-xs btn-outline"
                              onClick={() => removeLayoutDivider(index)}
                              disabled={layoutSaving}
                            >
                              Удалить линию
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  }

                  const s = row.student;
                  studentNum += 1;
                  const zebraClass = studentNum % 2 === 0
                    ? 'teacher-students-row--even'
                    : 'teacher-students-row--odd';
                  const selectedClass = selectedStudentId === s.id ? ' selected' : '';
                  const lessonActive = Boolean(s.active_lesson_session_id || s.has_active_lesson_session);
                  return (
                    <tr
                      key={row.key}
                      className={`${zebraClass}${selectedClass}`.trim()}
                      onClick={reorderMode ? undefined : (e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                        navigate(`/teacher/students/${s.id}`);
                      }}
                      style={{ cursor: reorderMode ? 'default' : 'pointer' }}
                    >
                      {reorderMode && (
                        <td className="teacher-students-order-col" onClick={(e) => e.stopPropagation()}>
                          <div className="teacher-students-order-btns">
                            <button
                              type="button"
                              className="btn btn-xs btn-outline"
                              onClick={() => moveLayoutRow(index, -1)}
                              disabled={layoutSaving || index === 0}
                              title="Выше"
                              aria-label="Выше"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="btn btn-xs btn-outline"
                              onClick={() => moveLayoutRow(index, 1)}
                              disabled={layoutSaving || index === studentRows.length - 1}
                              title="Ниже"
                              aria-label="Ниже"
                            >
                              ↓
                            </button>
                          </div>
                        </td>
                      )}
                      {!reorderMode && (
                        <td className="teacher-students-num-col">{studentNum}</td>
                      )}
                      <td>
                        <div className="student-alias">
                          {reorderMode ? (
                            <span
                              className="student-alias-label"
                              title={s.username ? `@${s.username}` : undefined}
                            >
                              {studentLabelWithExamYear(s) || 'Ученик'}
                            </span>
                          ) : (
                            <Link
                              to={`/teacher/students/${s.id}`}
                              className="student-alias-label"
                              title={s.username ? `@${s.username}` : undefined}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {studentLabelWithExamYear(s) || 'Ученик'}
                            </Link>
                          )}
                        </div>
                      </td>
                      {reorderMode ? (
                        <td className="text-secondary" style={{ fontSize: '0.85rem' }}>
                          {s.relation_status === 'active' ? '' : s.relation_status}
                        </td>
                      ) : (
                        <>
                          <td>
                            <StudentMessengerCell
                              studentId={s.id}
                              messenger={s.messenger}
                              onUpdated={(messenger) => {
                                setStudents((prev) => prev.map((r) => (
                                  r.id === s.id ? { ...r, messenger } : r
                                )));
                              }}
                            />
                          </td>
                          <td className="teacher-students-telemost-col">
                            <StudentTelemostCell
                              studentId={s.id}
                              telemostUrl={s.telemost_url}
                              onUpdated={(telemost_url) => {
                                setStudents((prev) => prev.map((r) => (
                                  r.id === s.id ? { ...r, telemost_url } : r
                                )));
                              }}
                            />
                          </td>
                          <td>
                            <Link
                              to={`/teacher/billing/${s.id}`}
                              className={`teacher-student-balance ${
                                Number(s.effective_balance) < 0
                                  ? 'text-error'
                                  : Number(s.effective_balance) > 0
                                    ? 'text-success'
                                    : ''
                              }`.trim()}
                              onClick={(e) => e.stopPropagation()}
                              title={
                                Number(s.pending_amount) > 0
                                  ? `На счёте ${formatMoney(s.balance)}, ещё не подтверждено ${formatMoney(s.pending_amount)}`
                                  : 'Открыть оплату ученика'
                              }
                            >
                              {formatMoney(s.effective_balance)}
                            </Link>
                          </td>
                          <td title="Несданные списки">{s.task_lists_count}</td>
                          <td>{s.is_online ? 'Да' : 'Нет'}</td>
                          <td
                            className="teacher-students-actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="teacher-students-actions-row">
                              {s.work_join_url ? (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary teacher-students-action-btn teacher-students-work-btn"
                                  onClick={() => navigate(s.work_join_url)}
                                  title="Режим наблюдения: та же задача, список и совместная сессия редактора, что у ученика"
                                >
                                  К работе
                                </button>
                              ) : (
                                <span
                                  className="text-secondary teacher-students-action-slot"
                                  title="Ученик не на странице работы из списка урока/ДЗ или редактор ещё не подключился"
                                >
                                  нет связи
                                </span>
                              )}
                              <button
                                type="button"
                                className={`btn btn-sm btn-outline btn-icon-only teacher-students-action-btn teacher-students-questionnaire-btn${s.has_questionnaire ? ' is-filled' : ''}`}
                                onClick={() => navigate(`/teacher/students/${s.id}/questionnaire`)}
                                title={s.has_questionnaire ? 'Анкета заполнена' : 'Анкета ещё не заполнена'}
                                aria-label={s.has_questionnaire ? 'Анкета заполнена' : 'Анкета ещё не заполнена'}
                              >
                                <i className="fa-solid fa-clipboard-list" aria-hidden />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline btn-icon-only teacher-students-action-btn"
                                onClick={() => navigate(`/teacher/students/${s.id}/activity`)}
                                title="Активность на сайте"
                                aria-label="Активность на сайте"
                              >
                                <i className="fa-solid fa-chart-line" aria-hidden />
                              </button>
                              {lessonActive ? (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline teacher-students-action-btn teacher-students-lesson-btn"
                                  disabled={endingLessonId === s.active_lesson_session_id || dashIssueSaving}
                                  onClick={() => requestEndLessonForStudent(s)}
                                  title="Завершить текущий урок"
                                >
                                  {endingLessonId === s.active_lesson_session_id ? '…' : 'Завершить урок'}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline teacher-students-action-btn teacher-students-lesson-btn"
                                  onClick={() => handleStartLesson(s.id)}
                                >
                                  Начать урок
                                </button>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="teacher-tab-sidebar">
          <div className="student-overview-block">
            <h3>Ссылка для учеников</h3>
            <p className="text-secondary" style={{ marginBottom: '0.75rem' }}>
              Одна ссылка: нет аккаунта — зарегистрируется и привяжется, аккаунт уже есть — только привяжется.
            </p>
            {user?.username ? (
              <div className="invite-link-row">
                <code className="invite-url">{getRegisterInviteUrl()}</code>
                <button
                  type="button"
                  className="btn btn-xs btn-outline"
                  onClick={async () => {
                    const url = getRegisterInviteUrl();
                    try {
                      await navigator.clipboard.writeText(url);
                      // eslint-disable-next-line no-alert
                      alert('Ссылка скопирована в буфер обмена.');
                    } catch {
                      // eslint-disable-next-line no-alert
                      alert(url);
                    }
                  }}
                >
                  Копировать
                </button>
              </div>
            ) : (
              <p className="text-secondary">Загрузка…</p>
            )}
          </div>

          <h3>Сводка по ученику</h3>
          {studentOverviewLoading && <p className="text-secondary">Загрузка...</p>}
          {studentOverviewError && <div className="alert alert-error">{studentOverviewError}</div>}
          {!studentOverviewLoading && !studentOverview && !studentOverviewError && (
            <p className="text-secondary">Выберите ученика слева, чтобы посмотреть детали.</p>
          )}
          {studentOverview && (
            <div className="student-overview">
              <div className="student-overview-header">
                <strong>
                  {studentLabel(studentOverview.student)}
                </strong>
                <div className="student-overview-sub">
                  @{studentOverview.student.username}
                </div>
              </div>

              <div className="student-overview-block">
                <h4>Активный урок</h4>
                {studentOverview.active_lesson_session ? (
                  <div className="badge">
                    Урок #{studentOverview.active_lesson_session.id} с{' '}
                    {new Date(studentOverview.active_lesson_session.started_at).toLocaleString('ru-RU')}
                  </div>
                ) : (
                  <p className="text-secondary">Сейчас урок не идёт.</p>
                )}
              </div>

              <div className="student-overview-block">
                <h4>Списки задач</h4>
                {studentOverview.task_lists.length === 0 ? (
                  <p className="text-secondary">Для этого ученика ещё нет списков задач.</p>
                ) : (
                  <ul className="list-simple">
                    {studentOverview.task_lists.map((lst) => {
                      const firstItem = Array.isArray(lst.items) && lst.items.length > 0 ? lst.items[0] : null;
                      const firstTaskSlug = firstItem?.task ? taskNavRef(firstItem.task) : null;
                      const hasActiveLesson = Boolean(studentOverview.active_lesson_session);
                      const workUrl = firstTaskSlug
                        ? hasActiveLesson
                          ? `/sessions/${studentOverview.active_lesson_session.id}/tasks/${firstTaskSlug}?list=${lst.id}`
                          : `/work/${firstTaskSlug}?list=${lst.id}`
                        : null;
                      return (
                        <li key={lst.id}>
                          <span>{lst.title}</span>{' '}
                          <span className="badge">
                            {lst.student_status}
                            {lst.student_finished_at ? ' • Сдано' : ''}
                          </span>{' '}
                          {workUrl && (
                            <Link to={workUrl} className="btn btn-xs btn-outline">
                              Открыть в работе
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="student-overview-block">
                <h4>Последние попытки</h4>
                {studentOverview.recent_attempts.length === 0 ? (
                  <p className="text-secondary">Пока нет зафиксированных попыток.</p>
                ) : (
                  <ul className="list-simple">
                    {studentOverview.recent_attempts.map((a) => (
                      <li key={a.id}>
                        <span>{a.task_title}</span>{' '}
                        <span className="badge">{a.self_report_status || '!'}</span>{' '}
                        <span className="text-secondary">
                          {a.created_at ? new Date(a.created_at).toLocaleString('ru-RU') : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderTaskListsTab() {
    return (
      <div className="teacher-tab teacher-tasklists">
        <div className="teacher-tasklists-header">
          <div className="teacher-tasklists-header-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/teacher/lists/create')}
              disabled={students.length === 0}
            >
              Создать список
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/teacher/lists/create?mode=template')}
            >
              Создать заготовку
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={resetTaskListFilters}
              disabled={taskListsLoading}
            >
              Сбросить фильтры
            </button>
          </div>
        </div>
        {taskListsError && <div className="alert alert-error">{taskListsError}</div>}
        <div className="teacher-tasklists-content">
          <div className="teacher-tasklists-list">
            {taskListsLoading ? (
              <p className="text-secondary">Загрузка списков задач...</p>
            ) : taskLists.length === 0 ? (
              <p className="text-secondary">Списков задач пока нет.</p>
            ) : (
              <>
                <div className="teacher-tasklists-table-wrap">
                  <table className="table teacher-tasklists-table">
                    <thead>
                      <tr>
                        <th>Название</th>
                        <th>Ученик</th>
                        <th>Выдано</th>
                        <th>Дедлайн</th>
                        <th>Статус ученика</th>
                        <th>Статус преподавателя</th>
                        <th>Задачи</th>
                        <th></th>
                      </tr>
                      <tr className="teacher-tasklists-filters">
                        <th>
                          <input
                            className="form-input teacher-tasklists-filter-input"
                            type="search"
                            value={taskListFilters.title}
                            onChange={(e) => updateTaskListFilter('title', e.target.value)}
                            placeholder="Поиск…"
                            aria-label="Фильтр по названию"
                          />
                        </th>
                        <th>
                          <input
                            className="form-input teacher-tasklists-filter-input"
                            type="search"
                            value={taskListFilters.student}
                            onChange={(e) => updateTaskListFilter('student', e.target.value)}
                            placeholder="Поиск…"
                            aria-label="Фильтр по ученику"
                          />
                        </th>
                        <th>
                          <div className="teacher-tasklists-date-range">
                            <input
                              className="form-input teacher-tasklists-filter-input"
                              type="date"
                              value={taskListFilters.issuedFrom}
                              onChange={(e) => updateTaskListFilter('issuedFrom', e.target.value)}
                              aria-label="Выдано с"
                              title="Выдано с"
                            />
                            <input
                              className="form-input teacher-tasklists-filter-input"
                              type="date"
                              value={taskListFilters.issuedTo}
                              onChange={(e) => updateTaskListFilter('issuedTo', e.target.value)}
                              aria-label="Выдано по"
                              title="Выдано по"
                            />
                          </div>
                        </th>
                        <th>
                          <div className="teacher-tasklists-date-range">
                            <input
                              className="form-input teacher-tasklists-filter-input"
                              type="date"
                              value={taskListFilters.deadlineFrom}
                              onChange={(e) => updateTaskListFilter('deadlineFrom', e.target.value)}
                              aria-label="Дедлайн с"
                              title="Дедлайн с"
                            />
                            <input
                              className="form-input teacher-tasklists-filter-input"
                              type="date"
                              value={taskListFilters.deadlineTo}
                              onChange={(e) => updateTaskListFilter('deadlineTo', e.target.value)}
                              aria-label="Дедлайн по"
                              title="Дедлайн по"
                            />
                          </div>
                        </th>
                        <th>
                          <select
                            className="form-select teacher-tasklists-filter-input"
                            value={taskListFilters.studentStatus}
                            onChange={(e) => updateTaskListFilter('studentStatus', e.target.value)}
                            aria-label="Статус ученика"
                          >
                            <option value="">Все</option>
                            <option value="IN_PROGRESS">В работе</option>
                            <option value="DONE">Завершён</option>
                          </select>
                        </th>
                        <th>
                          <select
                            className="form-select teacher-tasklists-filter-input"
                            value={taskListFilters.teacherStatus}
                            onChange={(e) => updateTaskListFilter('teacherStatus', e.target.value)}
                            aria-label="Статус преподавателя"
                          >
                            <option value="">Все</option>
                            <option value="NOT_CHECKED">Не проверен</option>
                            <option value="CHECKED">Проверен</option>
                            <option value="ARCHIVED">В архиве</option>
                          </select>
                        </th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTaskLists.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-secondary">
                            Нет списков по текущим фильтрам.
                          </td>
                        </tr>
                      ) : (
                        filteredTaskLists.map((lst) => (
                          <tr key={lst.id}>
                            <td>{lst.title}</td>
                            <td>{listStudentDisplay(lst)}</td>
                            <td>{lst.issued_at ? new Date(lst.issued_at).toLocaleDateString('ru-RU') : '—'}</td>
                            <td>{lst.deadline_at ? new Date(lst.deadline_at).toLocaleDateString('ru-RU') : '—'}</td>
                            <td>
                              {STUDENT_STATUS_LABELS[lst.student_status] || lst.student_status}
                              {lst.student_finished_at ? (
                                <div className="text-secondary" style={{ fontSize: '0.85rem' }}>
                                  Сдано: {new Date(lst.student_finished_at).toLocaleString('ru-RU')}
                                </div>
                              ) : null}
                            </td>
                            <td>{TEACHER_STATUS_LABELS[lst.teacher_status] || lst.teacher_status}</td>
                            <td>{Array.isArray(lst.items) ? lst.items.length : 0}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-xs btn-outline"
                                onClick={() => startEditList(lst)}
                              >
                                Редактировать
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="text-secondary teacher-tasklists-count">
                  Показано {filteredTaskLists.length} из {taskLists.length}
                </p>
              </>
            )}
          </div>

          {editingList && (
            <div className="teacher-tasklists-form">
              <h3>{editingList.id ? 'Редактирование списка задач' : 'Новый список задач'}</h3>
              {listFormError && <div className="alert alert-error">{listFormError}</div>}

              {!editingList.id && (
                <div className="form-group">
                  <label>Ученик</label>
                  <select
                    className="form-select"
                    value={listForm.studentId}
                    onChange={(e) =>
                      setListForm((prev) => ({ ...prev, studentId: e.target.value }))
                    }
                  >
                    <option value="">Выберите ученика</option>
                    {students.map((s) => (
                      <option key={s.id} value={s.id}>
                        {studentLabelWithLogin(s)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Название списка</label>
                <input
                  className="form-input"
                  type="text"
                  value={listForm.title}
                  onChange={(e) =>
                    setListForm((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="Например, «ДЗ 12/03»"
                />
              </div>

              <div className="form-group">
                <label>Дедлайн</label>
                <input
                  className="form-input"
                  type="date"
                  value={listForm.deadline}
                  onChange={(e) =>
                    setListForm((prev) => ({ ...prev, deadline: e.target.value }))
                  }
                />
              </div>

              <div className="form-group">
                <label>Задачи в списке</label>
                {listForm.items.length === 0 ? (
                  <p className="text-secondary">Пока нет задач. Добавьте задачи через поиск ниже.</p>
                ) : (
                  <ul className="list-simple">
                    {listForm.items.map((it) => (
                      <li key={it.taskId}>
                        <div className="tasklist-item-row">
                          <span>
                            {it.title || it.slug || (it.taskId != null ? `ID ${it.taskId}` : '')}
                          </span>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Комментарий преподавателя (необязательно)"
                            value={it.teacherNote}
                            onChange={(e) => updateItemNote(it.taskId, e.target.value)}
                          />
                          <button
                            type="button"
                            className="btn btn-xs btn-outline"
                            onClick={() => removeTaskFromList(it.taskId)}
                          >
                            Удалить
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="form-group">
                <label>Поиск задач для добавления</label>
                <div className="task-search-row">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Поиск по названию/условию"
                    value={taskSearch}
                    onChange={(e) => setTaskSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        searchTasks();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={searchTasks}
                    disabled={taskSearchLoading}
                  >
                    {taskSearchLoading ? 'Поиск…' : 'Найти'}
                  </button>
                </div>
                {taskSearchError && (
                  <div className="alert alert-error">{taskSearchError}</div>
                )}
                {taskSearchResults.length > 0 && (
                  <div className="task-search-results">
                    <ul className="list-simple">
                      {taskSearchResults.slice(0, 20).map((t) => (
                        <li key={t.id}>
                          <div className="task-search-item">
                            <span>
                              {t.title || taskNavRef(t)}{' '}
                              <span className="text-secondary">({taskNavRef(t)})</span>
                            </span>
                            <button
                              type="button"
                              className="btn btn-xs btn-outline"
                              onClick={() => addTaskToList(t)}
                            >
                              Добавить
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveList}
                  disabled={listSaving}
                >
                  {listSaving ? 'Сохранение…' : 'Сохранить список'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setEditingList(null)}
                  disabled={listSaving}
                >
                  Отмена
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const REPORT_LABELS = { EASY: '++', OK_NO_PEEK: '+', OK_WITH_PEEK: '+-', OK_WITH_QUESTION: '+?', FAILED: '-', NO_IDEAS: '--', NOT_TRIED: '!' };

  async function loadLessonReport(sessionId) {
    if (lessonReportSessionId === sessionId && lessonReport) {
      setLessonReportSessionId(null);
      setLessonReport(null);
      return;
    }
    setLessonReportSessionId(sessionId);
    setLessonReport(null);
    setLessonReportLoading(true);
    try {
      const res = await api.get(`/teacher/lesson-sessions/${sessionId}/report/`);
      setLessonReport(res.data);
    } catch {
      setLessonReport(null);
    } finally {
      setLessonReportLoading(false);
    }
  }

  function renderLessonsTab() {
    return (
      <div className="teacher-tab teacher-lessons">
        {lessonsError && <div className="alert alert-error">{lessonsError}</div>}
        {lessonsLoading ? (
          <p className="text-secondary">Загрузка уроков...</p>
        ) : lessons.length === 0 ? (
          <p className="text-secondary">Уроков пока нет.</p>
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Ученик</th>
                  <th>Начало</th>
                  <th>Окончание</th>
                  <th>Статус</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lessons.map((s) => (
                  <tr key={s.id}>
                    <td>{s.id}</td>
                    <td>{s.student_username}</td>
                    <td>{s.started_at ? new Date(s.started_at).toLocaleString('ru-RU') : '—'}</td>
                    <td>{s.ended_at ? new Date(s.ended_at).toLocaleString('ru-RU') : '—'}</td>
                    <td>{s.ended_at ? 'Завершён' : 'Активен'}</td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
                        {!s.ended_at && (
                          <button
                            type="button"
                            className="btn btn-xs btn-outline"
                            disabled={endingLessonId === s.id || lessonReportLoading || dashIssueSaving}
                            onClick={() => requestEndLessonFromList(s)}
                          >
                            {endingLessonId === s.id ? '…' : 'Завершить'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-xs btn-outline"
                          onClick={() => loadLessonReport(s.id)}
                          disabled={lessonReportLoading}
                        >
                          {lessonReportSessionId === s.id ? 'Скрыть отчёт' : 'Отчёт'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {lessonReportSessionId && (
              <div className="lesson-report-block">
                {lessonReportLoading ? (
                  <p className="text-secondary">Загрузка отчёта...</p>
                ) : lessonReport ? (
                  <div>
                    <h4>Отчёт по уроку #{lessonReport.session_id} ({lessonReport.student_username})</h4>
                    <p className="text-secondary">
                      {lessonReport.started_at ? new Date(lessonReport.started_at).toLocaleString('ru-RU') : ''}
                      {lessonReport.ended_at ? ` — ${new Date(lessonReport.ended_at).toLocaleString('ru-RU')}` : ''}
                    </p>
                    {lessonReport.teacher_notes?.trim() ? (
                      <div
                        className="lesson-teacher-notes"
                        style={{ whiteSpace: 'pre-wrap', marginBottom: '1rem' }}
                      >
                        <strong>Заметки по уроку</strong>
                        <p style={{ margin: '0.35rem 0 0' }}>{lessonReport.teacher_notes}</p>
                      </div>
                    ) : null}
                    {lessonReport.items?.length === 0 ? (
                      <p className="text-secondary">В этом уроке пока нет зафиксированных попыток по задачам.</p>
                    ) : (
                      <ul className="lesson-report-items">
                        {lessonReport.items.map((item) => (
                          <li key={item.attempt_id}>
                            <strong>{item.task_title || item.task_slug}</strong>
                            {' — '}
                            <span className="report-badge">{REPORT_LABELS[item.self_report_status] || item.self_report_status || '!'}</span>
                            {item.duration_minutes != null && ` (${item.duration_minutes} мин)`}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <p className="text-secondary">Не удалось загрузить отчёт.</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="teacher-dashboard">
      <div className="page-header">
        <h1>{pageMeta.title}</h1>
        <p className="page-subtitle">{pageMeta.subtitle}</p>
      </div>

      {activeTab === 'students' && renderStudentsTab()}
      {activeTab === 'tasklists' && renderTaskListsTab()}
      {activeTab === 'lessons' && renderLessonsTab()}

      <Modal
        open={Boolean(endLessonTarget) && !dashIssueOpen}
        title="В черновике есть задачи"
        onClose={() => {
          if (endingLessonId || dashIssueSaving) return;
          setEndLessonTarget(null);
        }}
      >
        <p className="text-secondary">
          Урок #{endLessonTarget?.id}: выдать черновик как ДЗ или завершить без выдачи?
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={dashIssueSaving || Boolean(endingLessonId)}
            onClick={openDashIssueAndEnd}
          >
            Выдать и завершить…
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={dashIssueSaving || Boolean(endingLessonId)}
            onClick={() => void endLessonFromList(endLessonTarget.id)}
          >
            {endingLessonId === endLessonTarget?.id ? '…' : 'Завершить без выдачи'}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={dashIssueSaving || Boolean(endingLessonId)}
            onClick={() => setEndLessonTarget(null)}
          >
            Отмена
          </button>
        </div>
      </Modal>

      <Modal
        open={dashIssueOpen}
        title="Выдать ДЗ и завершить урок"
        onClose={() => {
          if (dashIssueSaving) return;
          setDashIssueOpen(false);
        }}
      >
        <label className="form-label">
          Название
          <input
            type="text"
            className="form-input"
            value={dashIssueTitle}
            onChange={(e) => setDashIssueTitle(e.target.value)}
          />
        </label>
        <label className="form-label" style={{ display: 'block', marginTop: '0.75rem' }}>
          Дедлайн (необязательно)
          <input
            type="date"
            className="form-input"
            value={dashIssueDeadline}
            onChange={(e) => setDashIssueDeadline(e.target.value)}
          />
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={dashIssueSaving}
            onClick={() => void submitDashIssueAndEnd()}
          >
            {dashIssueSaving ? '…' : 'Выдать и завершить'}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={dashIssueSaving}
            onClick={() => setDashIssueOpen(false)}
          >
            Назад
          </button>
        </div>
      </Modal>
    </div>
  );
}

